import {
  addDays,
  differenceInMinutes,
  eachDayOfInterval,
  format,
  getHours,
  getMinutes,
  parse,
  parseISO,
  set,
} from "date-fns";
import { ja } from "date-fns/locale";

export type BreakTime = {
  startBreakTime?: string | null | undefined;
  endBreakTime?: string | null | undefined;
};

export function getFromNextWeek(targetDay?: Date) {
  const today = targetDay ?? new Date();
  const nextWeek = addDays(today, 5);
  const dates = eachDayOfInterval({ start: today, end: nextWeek });
  return dates;
}

export function getFormatedDatetimes(dates: Date[], formatCode: string) {
  return dates.map((date) => format(date, formatCode));
}

export function getDayOfWeek(date: Date, formatCode?: string) {
  const code = formatCode ? formatCode : "EEEEE";
  const formattedDayOfWeek = format(date, code, { locale: ja });
  return formattedDayOfWeek;
}

export function getTimeDifferenceInMinutes(startTime: string, endTime: string) {
  const baseDate = new Date(2000, 0, 1); // 基準日を使用
  const startDate = parse(startTime, "HH:mm", baseDate);
  let endDate = parse(endTime, "HH:mm", baseDate);

  // 日をまたぐ場合の処理（終了時間が開始時間より早い場合）
  if (endDate < startDate) {
    endDate = addDays(endDate, 1);
  }

  return differenceInMinutes(endDate, startDate);
}

export function getBreakTimesDifferenceInMinutes(breakTimes: BreakTime[]) {
  return breakTimes
    .map((breakTime) => {
      if (breakTime.startBreakTime && breakTime.endBreakTime) {
        return getTimeDifferenceInMinutes(
          breakTime.startBreakTime,
          breakTime.endBreakTime,
        );
      } else {
        return 0;
      }
    })
    .reduce((sum, num) => sum + num, 0);
}

export const formatDate = (date: string | null | undefined): string => {
  if (!date) {
    return "-月-日";
  }
  const d = parseISO(date);
  return format(d, "M月d日");
};

export const formatTime = (time: string | null | undefined): string => {
  if (!time) {
    return "--:--";
  }
  const d = parseISO(time);
  return format(d, "HH:mm");
};

/**
 * 勤務時間（分）を計算する
 * @param startTime 開始時間（HH:mm形式）
 * @param endTime 終了時間（HH:mm形式）
 * @param breakTimes 休憩時間の配列
 * @returns 勤務時間（分）
 */
export function calculateWorkMinutes(
  startTime: string,
  endTime: string,
  breakTimes: BreakTime[],
): number {
  // getTimeDifferenceInMinutesを使用して統一
  const totalMinutes = getTimeDifferenceInMinutes(startTime, endTime);
  const breakMinutes = getBreakTimesDifferenceInMinutes(breakTimes);
  const workMinutes = totalMinutes - breakMinutes;

  return Math.max(0, workMinutes); // 負の値を防ぐ
}

/**
 * 時間文字列（HH:MM）を分に変換する
 * @param timeString 時間文字列（例: "09:30"）
 * @returns 分単位の数値
 */
export function timeStringToMinutes(timeString: string): number {
  const baseDate = new Date(2000, 0, 1); // 基準日
  const parsedTime = parse(timeString, "HH:mm", baseDate);
  return getHours(parsedTime) * 60 + getMinutes(parsedTime);
}

/**
 * 日付と時間文字列（HH:mm形式）を結合してISO文字列に変換
 * @param day 基準となる日付
 * @param time 時間文字列（HH:mm形式、例: "14:00"）
 * @returns ISO文字列、またはundefined（timeがundefinedの場合）
 */
export function convertTimeStringToDate(
  day: Date,
  time: string | undefined,
): string | undefined {
  if (!time) {
    return undefined;
  }
  const targetDay = new Date(day);
  const [hours, minutes] = time.split(":");
  const date = set(targetDay, {
    hours: Number(hours),
    minutes: Number(minutes),
  });
  return date.toISOString();
}

/**
 * 業務日付のみをフォーマット
 * @param startWorkAt 開始日時
 * @param workDayOfWeek 曜日（例: "月"）
 * @returns フォーマットされた文字列（例: "1/15(月)"）
 */
export function formatWorkDate(
  startWorkAt: Date,
  workDayOfWeek: string,
): string {
  const dateStr = format(startWorkAt, "M/d", { locale: ja });
  return `${dateStr}(${workDayOfWeek})`;
}

/**
 * 業務日時をフォーマット（サーバー側のformat_work_dateロジックに合わせる）
 * @param startWorkAt 開始日時
 * @param endWorkAt 終了日時
 * @param workDayOfWeek 曜日（例: "月"）
 * @returns フォーマットされた文字列（例: "1/15(月) 9:00~17:00" または "1/15(月) 22:00~翌2:00"）
 */
export function formatWorkDateTime(
  startWorkAt: Date,
  endWorkAt: Date,
  workDayOfWeek: string,
): string {
  // 日が変わるかチェック（開始日の開始時刻と終了日の開始時刻を比較）
  const startDay = new Date(
    startWorkAt.getFullYear(),
    startWorkAt.getMonth(),
    startWorkAt.getDate(),
  );
  const endDay = new Date(
    endWorkAt.getFullYear(),
    endWorkAt.getMonth(),
    endWorkAt.getDate(),
  );
  const nextDayLabel = startDay.getTime() < endDay.getTime() ? "翌" : "";

  // 時間フォーマット: H:mm
  const startTimeStr = format(startWorkAt, "H:mm", { locale: ja });
  const endTimeStr = format(endWorkAt, "H:mm", { locale: ja });

  return `${formatWorkDate(startWorkAt, workDayOfWeek)} ${startTimeStr}~${nextDayLabel}${endTimeStr}`;
}
